import { ContentItem } from '@examdojo/models/content-item';
import { StemMarkScheme } from '@examdojo/models/markscheme-v2';
import { OptionsItem, QuestionItemTypeEnum, QuestionItemV2, StemItemV2 } from '@examdojo/models/question';
import { getLetterLabel, getRomanLabel } from '../question-items-editor/stem-label/stem-label.model';

/**
 * Represents an MCQ with its hierarchical context
 */
export interface MCQInfo {
  /** Unique identifier for this MCQ */
  id: string;
  /** Human-readable display label */
  label: string;
  /** The actual options item containing the MCQ */
  optionsItem: OptionsItem;
  /** Path to this MCQ in the question structure */
  path: {
    /** Index of the parent question item */
    itemIndex: number;
  };
}

/**
 * Checks if question items contain any multiple choice questions (MCQ)
 */
export function isMultipleChoiceQuestion(questionItems: QuestionItemV2[]): boolean {
  return questionItems.some((item) => item.type === QuestionItemTypeEnum.Options);
}

/**
 * Extracts all MCQs from question items with hierarchical information
 */
export function extractMCQInfo(questionItems: QuestionItemV2[]): MCQInfo[] {
  const mcqs: MCQInfo[] = [];

  // First pass: collect all MCQ items
  const mcqItems: Array<{ item: OptionsItem; itemIndex: number }> = [];
  for (let itemIndex = 0; itemIndex < questionItems.length; itemIndex++) {
    const item = questionItems[itemIndex];
    if (item.type === 'options') {
      mcqItems.push({ item, itemIndex });
    }
  }

  // Second pass: generate labels (always just 'MCQ' since there's typically only one)
  mcqItems.forEach(({ item, itemIndex }) => {
    const label = 'MCQ';

    mcqs.push({
      id: `mcq_${itemIndex}`,
      label,
      optionsItem: item,
      path: { itemIndex },
    });
  });

  return mcqs;
}

/**
 * Represents a markscheme with its hierarchical context
 */
export interface MarkschemeInfo {
  /** Unique identifier for this markscheme */
  id: string;
  /** Display label like "Markscheme (a)" or "Markscheme (a) (i)" */
  label: string;
  /** The actual markscheme object */
  markscheme: StemMarkScheme;
  /** Path to this markscheme in the question structure */
  path: {
    /** Index of the parent question item */
    itemIndex: number;
    /** Index within stem group (if applicable) */
    stemIndex?: number;
  };
  /** Whether this markscheme is nested within a stem group */
  isNested: boolean;
}

/**
 * Counts the total number of markschemes in question items
 */
export function countMarkschemes(questionItems: QuestionItemV2[]): number {
  let count = 0;

  for (const item of questionItems) {
    if (item.type === 'stem') {
      if (item.markscheme) {
        count++;
      }
    } else if (item.type === 'stem_group') {
      // Count markschemes in all stems within the group
      count += item.stems.filter((stem) => stem.markscheme).length;
    }
    // stimulus items don't have markschemes
  }

  return count;
}

/**
 * Counts the total number of images across all question items
 * This includes images in:
 * - Stimulus content items
 * - Stem content items
 * - Sub-stem content items (within stem groups)
 * - Markscheme images (criterion and worked solutions)
 */
export function countTotalImages(questionItems: QuestionItemV2[]): number {
  let count = 0;

  for (const item of questionItems) {
    // Count images in the item's own content_items
    count += item.content_items.filter((contentItem) => contentItem.type === 'image').length;

    if (item.type === 'stem') {
      // Count images in stem markscheme
      count += countImagesInStemMarkscheme(item);
    } else if (item.type === 'stem_group') {
      // Count images in all stems within the group
      for (const stem of item.stems) {
        count += stem.content_items.filter((contentItem) => contentItem.type === 'image').length;
        count += countImagesInStemMarkscheme(stem);
      }
    }
  }

  return count;
}

/**
 * Helper function to count images within a stem's markscheme
 */
function countImagesInStemMarkscheme(stem: StemItemV2): number {
  if (!stem.markscheme) {
    return 0;
  }

  let count = 0;

  // Count images in mark groups criteria
  for (const markGroup of stem.markscheme.all_mark_groups) {
    count += markGroup.criterion.filter((contentItem) => contentItem.type === 'image').length;
  }

  // Count images in solution paths worked solutions
  for (const solutionPath of stem.markscheme.solution_paths) {
    count += solutionPath.worked_solution.filter((contentItem) => contentItem.type === 'image').length;
  }

  return count;
}

/**
 * Gets detailed breakdown of images by location
 */
export function getImageBreakdown(questionItems: QuestionItemV2[]): {
  stimulusImages: number;
  stemImages: number;
  markschemeImages: number;
  total: number;
} {
  let stimulusImages = 0;
  let stemImages = 0;
  let markschemeImages = 0;

  for (const item of questionItems) {
    if (item.type === 'stimulus') {
      stimulusImages += item.content_items.filter((contentItem) => contentItem.type === 'image').length;
    } else if (item.type === 'stem') {
      stemImages += item.content_items.filter((contentItem) => contentItem.type === 'image').length;
      markschemeImages += countImagesInStemMarkscheme(item);
    } else if (item.type === 'stem_group') {
      // Stem group's own content items count as stimulus-like content
      stimulusImages += item.content_items.filter((contentItem) => contentItem.type === 'image').length;

      // Count images in sub-stems
      for (const stem of item.stems) {
        stemImages += stem.content_items.filter((contentItem) => contentItem.type === 'image').length;
        markschemeImages += countImagesInStemMarkscheme(stem);
      }
    }
  }

  return {
    stimulusImages,
    stemImages,
    markschemeImages,
    total: stimulusImages + stemImages + markschemeImages,
  };
}

/**
 * Extracts all markschemes from question items with hierarchical information
 */
export function extractMarkschemeInfo(questionItems: QuestionItemV2[]): MarkschemeInfo[] {
  const markschemes: MarkschemeInfo[] = [];
  let stemCounter = 0; // Global counter for stem labeling (a, b, c, ...)

  for (let itemIndex = 0; itemIndex < questionItems.length; itemIndex++) {
    const item = questionItems[itemIndex];

    if (item.type === 'stem' && item.markscheme) {
      // Direct stem with markscheme
      const label = `Markscheme (${getLetterLabel(stemCounter)})`;
      markschemes.push({
        id: `markscheme_${itemIndex}`,
        label,
        markscheme: item.markscheme,
        path: { itemIndex },
        isNested: false,
      });
      stemCounter++;
    } else if (item.type === 'stem_group') {
      // Stem group with nested stems
      for (let stemIndex = 0; stemIndex < item.stems.length; stemIndex++) {
        const stem = item.stems[stemIndex];
        if (stem.markscheme) {
          const parentLabel = getLetterLabel(stemCounter);
          const childLabel = getRomanLabel(stemIndex);
          const label = `Markscheme (${parentLabel}) (${childLabel})`;

          markschemes.push({
            id: `markscheme_${itemIndex}_${stemIndex}`,
            label,
            markscheme: stem.markscheme,
            path: { itemIndex, stemIndex },
            isNested: true,
          });
        }
      }
      stemCounter++; // Increment for the entire stem group
    }
  }

  return markschemes;
}

/**
 * Represents an image with its hierarchical context
 */
export interface ImageInfo {
  /** Unique identifier for this image */
  id: string;
  /** Human-readable display label */
  label: string;
  /** The actual content item containing the image */
  contentItem: ContentItem;
  /** Path to this image in the question structure */
  path: {
    /** Index of the parent question item */
    itemIndex: number;
    /** Index within stem group (if applicable) */
    stemIndex?: number;
    /** Index within content_items array */
    contentIndex: number;
    /** Location type (stimulus, stem, markscheme, etc.) */
    location:
      | 'stimulus'
      | 'stem'
      | 'stem_group'
      | 'markscheme_criterion'
      | 'markscheme_solution'
      | 'options'
      | 'worked_solution';
    /** Additional context for markscheme images */
    markschemeContext?: {
      markGroupIndex?: number;
      solutionPathIndex?: number;
    };
    /** Additional context for options */
    optionContext?: {
      optionIndex: number;
    };
  };
}

/**
 * Represents a table with its hierarchical context
 * Extends ImageInfo since they have identical structure
 */
export interface TableInfo extends ImageInfo {}

/**
 * Generic function to extract content items of a specific type with hierarchical information
 */
function extractContentItemInfo<T extends ImageInfo | TableInfo>(
  questionItems: QuestionItemV2[],
  contentType: 'image' | 'table',
  itemTypeName: string,
): T[] {
  const items: T[] = [];
  let globalItemCounter = 0;
  let stemCounter = 0; // Global counter for stem labeling (a, b, c, ...)
  let stimulusCounter = 0; // Global counter for stimulus labeling (1, 2, 3, ...)

  for (let itemIndex = 0; itemIndex < questionItems.length; itemIndex++) {
    const item = questionItems[itemIndex];

    // Extract items from the item's own content_items
    // Use appropriate location based on item type
    const location: ImageInfo['path']['location'] =
      item.type === 'stem'
        ? 'stem'
        : item.type === 'stem_group'
          ? 'stem_group'
          : item.type === 'options'
            ? 'options'
            : 'stimulus';

    // Create numbered label for stimulus items and simple label for MCQ
    let baseLabel = getItemTypeLabel(item.type);
    if (item.type === QuestionItemTypeEnum.Stimulus) {
      stimulusCounter++;
      baseLabel = `Stimulus ${stimulusCounter}`;
    } else if (item.type === QuestionItemTypeEnum.Options) {
      baseLabel = `MCQ`;
    }

    const itemItems = extractContentItemsFromContentItems<T>(
      item.content_items,
      itemIndex,
      baseLabel,
      location,
      globalItemCounter,
      contentType,
      itemTypeName,
    );
    items.push(...itemItems);
    globalItemCounter += itemItems.length;

    // Extract items from individual MCQ options if this is an options item
    if (item.type === QuestionItemTypeEnum.Options && 'options' in item) {
      const optionsItem = item as OptionsItem;
      for (let optionIndex = 0; optionIndex < optionsItem.options.length; optionIndex++) {
        const option = optionsItem.options[optionIndex];
        const optionLabel = `${baseLabel} Option ${optionIndex + 1}`;
        const optionItems = extractContentItemsFromContentItems<T>(
          option.content_items,
          itemIndex,
          optionLabel,
          'options',
          globalItemCounter,
          contentType,
          itemTypeName,
          undefined, // stemIndex
          optionIndex, // Pass optionIndex for context
        );
        items.push(...optionItems);
        globalItemCounter += optionItems.length;
      }
    }

    if (item.type === 'stem' && item.markscheme) {
      // Extract items from stem markscheme
      const stemLabel = `Stem (${getLetterLabel(stemCounter)})`;
      const markschemeItems = extractContentItemsFromMarkscheme<T>(
        item.markscheme,
        itemIndex,
        stemLabel,
        globalItemCounter,
        contentType,
        itemTypeName,
      );
      items.push(...markschemeItems);
      globalItemCounter += markschemeItems.length;
      stemCounter++;
    } else if (item.type === 'stem_group') {
      // Extract items from stems within the group
      const parentLabel = getLetterLabel(stemCounter);

      for (let stemIndex = 0; stemIndex < item.stems.length; stemIndex++) {
        const stem = item.stems[stemIndex];
        const childLabel = getRomanLabel(stemIndex);
        const stemLabel = `Stem (${parentLabel}) (${childLabel})`;

        // Items from stem content
        const stemItems = extractContentItemsFromContentItems<T>(
          stem.content_items,
          itemIndex,
          stemLabel,
          'stem',
          globalItemCounter,
          contentType,
          itemTypeName,
          stemIndex,
        );
        items.push(...stemItems);
        globalItemCounter += stemItems.length;

        // Items from stem markscheme
        if (stem.markscheme) {
          const stemMarkschemeItems = extractContentItemsFromMarkscheme<T>(
            stem.markscheme,
            itemIndex,
            stemLabel,
            globalItemCounter,
            contentType,
            itemTypeName,
            stemIndex,
          );
          items.push(...stemMarkschemeItems);
          globalItemCounter += stemMarkschemeItems.length;
        }
      }
      stemCounter++; // Increment for the entire stem group
    } else if (item.type === 'stem') {
      // Handle direct stem without markscheme
      stemCounter++;
    }
  }

  return items;
}

/**
 * Helper function to extract content items from content items array
 */
function extractContentItemsFromContentItems<T extends ImageInfo | TableInfo>(
  contentItems: ContentItem[],
  itemIndex: number,
  baseLabel: string,
  location: ImageInfo['path']['location'],
  startItemCounter: number,
  contentType: 'image' | 'table',
  itemTypeName: string,
  stemIndex?: number,
  optionIndex?: number,
): T[] {
  const items: T[] = [];
  let itemCounter = 0;

  for (let contentIndex = 0; contentIndex < contentItems.length; contentIndex++) {
    const contentItem = contentItems[contentIndex];
    if (contentItem.type === contentType) {
      const label = `${baseLabel}: ${itemTypeName} ${itemCounter + 1}`;

      const path: ImageInfo['path'] = {
        itemIndex,
        stemIndex,
        contentIndex,
        location,
      };

      // Add option context if this is from an MCQ option
      if (optionIndex !== undefined) {
        path.optionContext = { optionIndex };
      }

      items.push({
        id: `${contentType}_${itemIndex}_${stemIndex ?? 'main'}_${contentIndex}${
          optionIndex !== undefined ? `_option_${optionIndex}` : ''
        }`,
        label,
        contentItem,
        path,
      } as T);
      itemCounter++;
    }
  }

  return items;
}

/**
 * Helper function to extract content items from markscheme
 */
function extractContentItemsFromMarkscheme<T extends ImageInfo | TableInfo>(
  markscheme: StemMarkScheme,
  itemIndex: number,
  baseLabel: string,
  startItemCounter: number,
  contentType: 'image' | 'table',
  itemTypeName: string,
  stemIndex?: number,
): T[] {
  const items: T[] = [];
  let itemCounter = 0;

  // Extract from mark groups criteria
  for (let markGroupIndex = 0; markGroupIndex < markscheme.all_mark_groups.length; markGroupIndex++) {
    const markGroup = markscheme.all_mark_groups[markGroupIndex];
    for (let contentIndex = 0; contentIndex < markGroup.criterion.length; contentIndex++) {
      const contentItem = markGroup.criterion[contentIndex];
      if (contentItem.type === contentType) {
        const label = `${baseLabel} Markscheme: ${itemTypeName} ${itemCounter + 1}`;

        items.push({
          id: `${contentType}_${itemIndex}_${
            stemIndex ?? 'main'
          }_markscheme_criterion_${markGroupIndex}_${contentIndex}`,
          label,
          contentItem,
          path: {
            itemIndex,
            stemIndex,
            contentIndex,
            location: 'markscheme_criterion',
            markschemeContext: { markGroupIndex },
          },
        } as T);
        itemCounter++;
      }
    }
  }

  // Extract from solution paths worked solutions
  for (let solutionIndex = 0; solutionIndex < markscheme.solution_paths.length; solutionIndex++) {
    const solutionPath = markscheme.solution_paths[solutionIndex];
    for (let contentIndex = 0; contentIndex < solutionPath.worked_solution.length; contentIndex++) {
      const contentItem = solutionPath.worked_solution[contentIndex];
      if (contentItem.type === contentType) {
        const label = `${baseLabel} Markscheme Solution: ${itemTypeName} ${itemCounter + 1}`;

        items.push({
          id: `${contentType}_${itemIndex}_${stemIndex ?? 'main'}_markscheme_solution_${solutionIndex}_${contentIndex}`,
          label,
          contentItem,
          path: {
            itemIndex,
            stemIndex,
            contentIndex,
            location: 'markscheme_solution',
            markschemeContext: { solutionPathIndex: solutionIndex },
          },
        } as T);
        itemCounter++;
      }
    }
  }

  return items;
}

/**
 * Extracts all images from question items with hierarchical information
 */
export function extractImageInfo(questionItems: QuestionItemV2[]): ImageInfo[] {
  return extractContentItemInfo<ImageInfo>(questionItems, 'image', 'Image');
}

/**
 * Extracts all tables from question items with hierarchical information
 */
export function extractTableInfo(questionItems: QuestionItemV2[]): TableInfo[] {
  return extractContentItemInfo<TableInfo>(questionItems, 'table', 'Table');
}

/**
 * Helper function to get human-readable item type label
 */
function getItemTypeLabel(type: QuestionItemTypeEnum): string {
  switch (type) {
    case QuestionItemTypeEnum.Stimulus:
      return 'Stimulus';
    case QuestionItemTypeEnum.Stem:
      return 'Stem';
    case QuestionItemTypeEnum.StemGroup:
      return 'Stem Group';
    case QuestionItemTypeEnum.Options:
      return 'Options';
    default:
      return String(type).toLowerCase();
  }
}
